import { FieldShell } from '@today/ui-components'
import { Button, buttonVariants, cn, Input } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Where the TOTP enrolment journey stands (TO-63 REQ-013). */
export enum MfaEnrolmentStep {
	/** Exchanging the code and starting the association. */
	LOADING = 'loading',
	/** The journey cannot continue; `error` says why. */
	FAILED = 'failed',
	/** The QR code and secret are shown; the user types the 6-digit code. */
	CODE = 'code',
	/** The code was accepted: TOTP is enrolled. */
	DONE = 'done',
}

/** Copy of {@link MfaEnrolmentStepView}; each omitted key falls back to English. */
export interface MfaEnrolmentStepLabels {
	title: string
	loading: string
	scan: string
	qrAlt: string
	secretLabel: string
	codeLabel: string
	verify: string
	done: string
	back: string
}

const DEFAULT_LABELS: MfaEnrolmentStepLabels = {
	title: 'Set up your authenticator app',
	loading: 'Preparing your authenticator setup…',
	scan: 'Scan this QR code with your authenticator app, then enter the 6-digit code it shows.',
	qrAlt: 'QR code for your authenticator app',
	secretLabel: 'Or enter this key manually',
	codeLabel: '6-digit code',
	verify: 'Verify',
	done: 'Your authenticator app is set up.',
	back: 'Back to settings',
}

/**
 * Props for {@link MfaEnrolmentStepView} — controlled: the app owns the journey and its calls.
 *
 * @property qrCodeSrc - An image URL (typically a `data:` URL) of the `otpauth://` QR code.
 * @property secretCode - The TOTP secret, shown as text for manual entry.
 * @property code - The 6-digit field's raw text.
 * @property codeError - An inline refusal of the last code (the field stays usable).
 * @property error - Why the journey failed (step `FAILED`).
 * @property backHref - Where "Back to settings" leads.
 */
export interface MfaEnrolmentStepViewProps {
	step: MfaEnrolmentStep
	qrCodeSrc?: string | null
	secretCode?: string | null
	code: string
	codeError?: string | null
	error?: string | null
	isBusy?: boolean
	onCodeChange: (code: string) => void
	onSubmit: () => void
	backHref: string
	labels?: Partial<MfaEnrolmentStepLabels>
}

function CodeStep(props: MfaEnrolmentStepViewProps & { text: MfaEnrolmentStepLabels }): ReactElement {
	const { text } = props
	return (
		<div className="flex flex-col gap-4">
			<p className="text-ds-sm text-ds-text">{text.scan}</p>
			{props.qrCodeSrc ? (
				<img src={props.qrCodeSrc} alt={text.qrAlt} className="size-48 rounded-ds-md border border-ds-border" />
			) : null}
			<figure className="flex flex-col gap-1">
				<figcaption className="text-ds-sm text-ds-text-muted">{text.secretLabel}</figcaption>
				<code className="break-all rounded-ds-md border border-ds-border bg-ds-surface-muted p-3 font-mono text-ds-sm text-ds-text select-all">
					{props.secretCode}
				</code>
			</figure>
			<form
				// The app validates and words the refusals itself.
				noValidate
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault()
					props.onSubmit()
				}}
			>
				<FieldShell label={text.codeLabel} error={props.codeError ?? undefined}>
					<Input
						type="text"
						inputMode="numeric"
						autoComplete="one-time-code"
						maxLength={6}
						value={props.code}
						onChange={(e) => props.onCodeChange(e.target.value)}
					/>
				</FieldShell>
				<div className="flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{text.verify}
					</Button>
				</div>
			</form>
		</div>
	)
}

function StepBody(props: MfaEnrolmentStepViewProps & { text: MfaEnrolmentStepLabels }): ReactElement {
	const { text } = props
	switch (props.step) {
		case MfaEnrolmentStep.LOADING:
			return (
				<p className="text-ds-sm text-ds-text-muted" aria-live="polite">
					{text.loading}
				</p>
			)
		case MfaEnrolmentStep.FAILED:
			return (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			)
		case MfaEnrolmentStep.DONE:
			return (
				<p role="status" className="text-ds-sm font-medium text-ds-success">
					{text.done}
				</p>
			)
		default:
			return <CodeStep {...props} />
	}
}

/** One step of the TOTP enrolment journey: loading, QR + secret + code, failure, or done (organism, TO-63). */
export function MfaEnrolmentStepView(props: MfaEnrolmentStepViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	const showBack = props.step === MfaEnrolmentStep.FAILED || props.step === MfaEnrolmentStep.DONE
	return (
		<section className="flex max-w-xl flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6">
			<h1 className="text-ds-lg font-semibold text-ds-text">{text.title}</h1>
			<StepBody {...props} text={text} />
			{showBack ? (
				<a href={props.backHref} className={cn(buttonVariants({ variant: 'secondary', size: 'md' }), 'self-start')}>
					{text.back}
				</a>
			) : null}
		</section>
	)
}
